// =============================================
// Writing Profile Generator — auto-generate author profile from map data
// v6: LLM-powered profile generation using full map context
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { parseLLMJSON, getLLMProvider, getStageConfig } from "@/services/llm";
import { loadPrompt } from "@/services/llm/promptLoader";
import { compilePrompt } from "./promptCompiler";
import { logStage, logProfile, logPublic, logDebug, logError } from "./contentLogger";
import type { ThematicWritingProfile } from "@/types/thematicMaps";

// =============================================
// Validation
// =============================================

const VALID_VOICE = new Set(["ty", "wy", "pan_pani", "bezosobowy"]);
const VALID_CLAIM = new Set(["cautious", "moderate", "bold"]);
const VALID_VERBOSITY = new Set(["concise", "standard", "detailed"]);
const VALID_SENTENCE = new Set(["short", "mixed", "long"]);
const VALID_GLOSSARY = new Set(["first_mention", "always", "never"]);

export function isProfileValid(profile: Record<string, unknown>): boolean {
  if (!profile) return false;
  const s = (field: string) => typeof profile[field] === "string" ? (profile[field] as string).length : 0;
  const a = (field: string) => Array.isArray(profile[field]) ? (profile[field] as unknown[]).length : 0;

  if (s("public_author_label") < 10) return false;
  if (s("author_identity") < 20) return false;
  if (s("mission") < 5) return false;
  if (!VALID_VOICE.has(profile.voice_pronouns as string)) return false;
  if (!VALID_CLAIM.has(profile.claim_strength as string)) return false;
  if (!VALID_VERBOSITY.has(profile.verbosity as string)) return false;
  if (!VALID_SENTENCE.has(profile.sentence_length as string)) return false;
  if (!VALID_GLOSSARY.has(profile.glossary_mode as string)) return false;
  // v7: voice_characteristics zamiast preferred_phrases/banned_phrases
  // Akceptuj profil jeśli ma ALBO stare pola ALBO nowe
  const hasOldPhrases = a("preferred_phrases") >= 3 && a("banned_phrases") >= 3;
  const hasNewVoice = a("voice_characteristics") >= 2;
  if (!hasOldPhrases && !hasNewVoice) return false;
  if (s("language_rules") < 10) return false;

  return true;
}

// =============================================
// Generator
// =============================================

export async function generateWritingProfile(
  mapId: string,
): Promise<Record<string, unknown>> {
  logStage("WRITING_PROFILE", "START", { mapId });

  // 1. Fetch full map data
  const { data: map } = await (supabase as any)
    .from("thematic_maps")
    .select("name, language, macro_statement, scope_in, scope_out, boundaries_owned, boundaries_not_owned")
    .eq("id", mapId)
    .single();

  if (!map) throw new Error("Map not found");

  const { data: seeds } = await (supabase as any)
    .from("thematic_seeds")
    .select("name, intent_family")
    .eq("map_id", mapId)
    .or("status.is.null,status.eq.active"); // M18: writing profile na podstawie aktywnych seedów

  const { data: nodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("title, document_role, primary_entity_raw")
    .eq("map_id", mapId);

  const { data: entities } = await (supabase as any)
    .from("thematic_entities")
    .select("id, canonical_name, entity_type, description")
    .eq("map_id", mapId);

  // EAV for top entities
  const entityIds = (entities || []).map((e: any) => e.id);
  let eav: any[] = [];
  if (entityIds.length > 0) {
    const { data: eavData } = await (supabase as any)
      .from("thematic_entity_attributes")
      .select("entity_id, attribute_name, value")
      .in("entity_id", entityIds)
      .gte("confidence", 0.7)
      .limit(30);
    // Resolve entity names
    const idToName = new Map<string, string>();
    for (const e of entities || []) idToName.set(e.id, e.canonical_name);
    eav = (eavData || []).map((a: any) => ({
      entity_name: idToName.get(a.entity_id) || a.entity_id,
      attribute_name: a.attribute_name,
      value: a.value,
    }));
  }

  // 2. Build template vars
  const templateVars: Record<string, string> = {
    domain_name: map.name || "",
    language: map.language || "pl",
    macro_statement: map.macro_statement || "",
    scope_in: (map.scope_in || []).join(", "),
    scope_out: (map.scope_out || []).join(", "),
    boundaries_owned: (map.boundaries_owned || []).join(", "),
    boundaries_not_owned: (map.boundaries_not_owned || []).join(", "),
    seed_names: (seeds || []).map((s: any) => s.name).join(", "),
    seed_intents: [...new Set((seeds || []).flatMap((s: any) => s.intent_family || []))].join(", "),
    sample_node_titles: (nodes || []).map((n: any) => `${n.title} [${n.document_role || "?"}] entity="${n.primary_entity_raw || "?"}"`).join("\n"),
    sample_nodes: (nodes || []).map((n: any) => `${n.title} [${n.document_role || "?"}] entity="${n.primary_entity_raw || "?"}"`).join("\n"),
    sample_entities: (entities || []).map((e: any) => `${e.canonical_name} (${e.entity_type || "concept"}): ${e.description || ""}`).join("\n"),
    sample_eav: eav.map((a: any) => `${a.entity_name}.${a.attribute_name} = ${a.value}`).join("\n"),
  };

  logPublic("PROFILE", `Generator input: map=${map.name}, seeds=${(seeds || []).length}, nodes=${(nodes || []).length}, entities=${(entities || []).length}, eav=${eav.length}`);

  // 3. Load prompt + compile
  const dbPrompt = await loadPrompt("writing_profile_generation" as any);
  const stageConfig = getStageConfig("writing_profile_generation" as any);
  const model = dbPrompt?.modelId || stageConfig?.model || "gpt-5.4-mini";
  const provider = dbPrompt?.provider || stageConfig?.provider || "openai";

  const FALLBACK_PROMPT = `Generate a writing profile for "${map.name}" (${map.language}). Macro: ${map.macro_statement}. Return JSON with: public_author_label, author_identity, mission, audience_primary, voice_pronouns, verbosity, sentence_length, analogy_frequency, analogy_domain, claim_strength, evidence_style, glossary_mode, language_rules, voice_characteristics[], content_personality.`;

  let prompt: string;
  if (dbPrompt) {
    try {
      prompt = compilePrompt(dbPrompt.template, templateVars, "writing_profile_generation");
    } catch {
      prompt = FALLBACK_PROMPT;
    }
  } else {
    prompt = FALLBACK_PROMPT;
  }

  logDebug("PROFILE", "prompt_full", { prompt_full: prompt });

  // 4. LLM call
  const start = Date.now();
  const llm = getLLMProvider(provider);
  const response = await llm.call(prompt, {
    model, temperature: 0.3,
    step: "writing_profile_generation", mapId,
  });
  const duration = Date.now() - start;

  logDebug("PROFILE", `LLM response: model=${model}, tokens_in=${response.tokensInput}, tokens_out=${response.tokensOutput}, cost=${response.costUsd?.toFixed(4)}, duration_ms=${duration}`);
  logDebug("PROFILE", "response_full", { response_full: response.text });

  // 5. Parse + validate
  const profileData = parseLLMJSON<Record<string, unknown>>(response.text);

  if (!isProfileValid(profileData)) {
    logPublic("PROFILE", `Generated profile FAILED validation — using anyway with defaults`);
  }

  // 6. Add meta
  profileData.profile_name = `${map.name} — auto v1`;
  profileData.generated_from = "auto_llm";
  profileData.model_used = model;
  profileData.schema_version = 1;

  // 7. Log generated profile details
  logPublic("PROFILE", `Wygenerowany profil:`);
  logPublic("PROFILE", `  author_identity: ${profileData.author_identity || "-"}`);
  logPublic("PROFILE", `  audience_primary: ${profileData.audience_primary || "-"}`);
  logPublic("PROFILE", `  voice_pronouns: ${profileData.voice_pronouns || "-"} | verbosity: ${profileData.verbosity || "-"} | sentence_length: ${profileData.sentence_length || "-"}`);
  logPublic("PROFILE", `  claim_strength: ${profileData.claim_strength || "-"} | evidence_style: ${profileData.evidence_style || "-"}`);
  logPublic("PROFILE", `  analogy_frequency: ${profileData.analogy_frequency || "-"} | analogy_domain: ${profileData.analogy_domain || "-"}`);
  logPublic("PROFILE", `  glossary_mode: ${profileData.glossary_mode || "-"}`);
  logPublic("PROFILE", `  voice_characteristics: ${Array.isArray(profileData.voice_characteristics) ? (profileData.voice_characteristics as string[]).join("; ") : "-"}`);
  logPublic("PROFILE", `  content_personality: ${profileData.content_personality || "-"}`);
  logPublic("PROFILE", `  language_rules: ${profileData.language_rules || "-"}`);
  logPublic("PROFILE", `  model: ${model} | tokens: ${response.tokensInput}→${response.tokensOutput} | cost: $${response.costUsd?.toFixed(4) || "?"} | ${duration}ms`);

  logStage("WRITING_PROFILE", "END", { model, cost: response.costUsd, duration_ms: duration, valid: isProfileValid(profileData) });

  return profileData;
}

// =============================================
// Ensure active profile exists
// =============================================

export async function ensureActiveWritingProfile(
  mapId: string,
): Promise<ThematicWritingProfile | null> {
  // 1. Check if map has active profile
  const { data: map } = await (supabase as any)
    .from("thematic_maps")
    .select("active_profile_id")
    .eq("id", mapId)
    .single();

  if (map?.active_profile_id) {
    const { data: existingProfile } = await (supabase as any)
      .from("thematic_writing_profiles")
      .select("*")
      .eq("id", map.active_profile_id)
      .single();

    if (existingProfile) {
      logProfile("ensureProfile", existingProfile);
      const profName = existingProfile.profile_name || existingProfile.name || "(unnamed)";
      logPublic("PROFILE", `ensureProfile: existing "${profName}"`);
      return existingProfile as ThematicWritingProfile;
    }
  }

  // 2. No valid profile — generate one
  logPublic("PROFILE", "ensureProfile: no active profile, generating...");
  try {
    const profileData = await generateWritingProfile(mapId);

    // Get user ID
    const { data: session } = await supabase.auth.getSession();
    const userId = session?.session?.user?.id;
    if (!userId) {
      logPublic("PROFILE", "ensureProfile: no user session, skipping insert");
      return null;
    }

    // Insert as new profile (regenerate = new record, not overwrite)
    const insertPayload = {
      user_id: userId,
      map_id: mapId,
      name: profileData.profile_name || `Auto Profile`,
      profile_name: profileData.profile_name,
      public_author_label: profileData.public_author_label || "",
      author_identity: profileData.author_identity || "",
      mission: profileData.mission || "",
      audience_primary: profileData.audience_primary || "",
      voice_pronouns: profileData.voice_pronouns || "ty",
      verbosity: profileData.verbosity || "standard",
      sentence_length: profileData.sentence_length || "mixed",
      analogy_frequency: profileData.analogy_frequency || "moderate",
      analogy_domain: profileData.analogy_domain || "",
      preferred_phrases: profileData.preferred_phrases || [],
      banned_phrases: profileData.banned_phrases || [],
      language_rules: profileData.language_rules || "",
      claim_strength: profileData.claim_strength || "moderate",
      evidence_style: profileData.evidence_style || "",
      glossary_mode: profileData.glossary_mode || "first_mention",
      voice_characteristics: profileData.voice_characteristics || [],
      content_personality: profileData.content_personality || "",
      generated_from: "auto_llm",
      model_used: profileData.model_used || "gpt-5.4-mini",
      schema_version: 1,
      language: "pl",
      is_active: true,
      version: 1,
    };

    const { data: inserted, error: insertErr } = await (supabase as any)
      .from("thematic_writing_profiles")
      .insert(insertPayload)
      .select()
      .single();

    if (insertErr || !inserted) {
      logError("PROFILE", "ensureProfile: insert failed", insertErr);
      return null;
    }

    // Set as active
    await (supabase as any)
      .from("thematic_maps")
      .update({ active_profile_id: inserted.id })
      .eq("id", mapId);

    logPublic("PROFILE", `ensureProfile: generated + set active, id=${inserted.id}`);
    return inserted as ThematicWritingProfile;
  } catch (err) {
    logError("PROFILE", "ensureProfile: generation failed", err);
    return null;
  }
}
